import { useState, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Typography } from '../typography';
import { Button } from '../button';
import { FileInput, FileRow } from '../input';
import { ImageGallery, ImageGalleryCard, fullScreenButtonClick, HideInput } from '../image-gallery';
import { ModalBase, ModalWrapper, ModalFooterButtons } from './ModalComponents';
import { enqueueSnackbar } from '../snackbar';
import { filesFormatValidator } from '../../utils';
import { useUpdateImagesMutation, useUpdateDocumentsMutation } from '../../hooks';
import { INPUT_ERRORS, tableDotsActions } from '../../const';
import { i18n } from '../../i18n';

export const uploadImagesModal: any = {};

interface ModalImagesProps {
  isVisible?: boolean;
  isFlowRow?: boolean;
}

export const ModalImages = styled.div<ModalImagesProps>`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-auto-rows: max-content;
  grid-gap: 2.4rem;
  overflow: overlay;

  ${(props) =>
    !props.isVisible &&
    css`
      display: none;
    `}

  ${(props) =>
    props.isFlowRow &&
    css`
      grid-template-columns: 1fr;
      grid-auto-flow: row;
      grid-row-gap: initial;
    `}
`;

export const UploadImagesModal = () => {
  const updateImagesMutation = useUpdateImagesMutation();
  const updateDocumentsMutation = useUpdateDocumentsMutation();
  const [open, setOpen] = useState(false);
  const [images, setImages] = useState<any>([]);
  const [files, setFiles] = useState<any>([]);
  const [zoomImageIndex, setZoomImageIndex] = useState(0);
  const inputRef = useRef<any>();
  const obj = useRef<any>({});

  useEffect(() => {
    if (open) return;
    setImages([]);
    setFiles([]);
  }, [open]);

  useEffect(() => {
    uploadImagesModal.open = () => {
      if (uploadImagesModal.val === 'torepair') return;

      obj.current.name = tableDotsActions.find((obj) => obj.value === uploadImagesModal.val)?.label;
      obj.current.number = uploadImagesModal.number;
      if (uploadImagesModal.oldFilesLength) {
        const fileTypeName =
          obj.current.name === i18n.t('dropdown.addImages')
            ? i18n.t('pages.modal.fileTypeImages')
            : i18n.t('pages.modal.fileTypeDocuments');
        obj.current.title = i18n.t('pages.modal.uploadImagesTitle', {
          number: obj.current.number,
          length: uploadImagesModal.oldFilesLength,
          fileType: fileTypeName,
        });
      } else {
        obj.current.title = obj.current.number;
      }

      if (obj.current.name !== '') setOpen(true);
    };
  }, []);

  const handleOnClose = () => {
    setOpen(!open);
  };

  const handleChangeFiles = (newImages: any, newFiles: any) => {
    const newImagesValues = [...images, ...newImages];

    if (
      obj.current.name === i18n.t('dropdown.addImages') &&
      filesFormatValidator(newFiles, 'Image')
    ) {
      enqueueSnackbar(filesFormatValidator(newFiles, 'Image'), 'warning');
      return;
    }

    setImages(newImagesValues);
    setFiles([...files, ...newFiles]);
    inputRef.current.type = 'text';
    inputRef.current.type = 'file';
  };

  const handleDeleteImageClick = (i: number) => {
    setImages(images.filter((_: any, index: number) => index !== i));
    setFiles(files.filter((_: any, index: number) => index !== i));
  };

  const handleZoomClick = (i: number) => {
    setZoomImageIndex(i);
    fullScreenButtonClick('upload-images-modal');
  };

  const handleSubmitClick = async () => {
    if (images.length > 10) {
      enqueueSnackbar(INPUT_ERRORS.NOT_VALID.MAX_UPLOAD_FILES, 'warning');
      return;
    }

    let mutate: any = updateImagesMutation.mutate;
    if (obj.current.name === i18n.t('dropdown.addDocuments'))
      mutate = updateDocumentsMutation.mutate;

    const data = images.map((img: any, i: any) => ({
      action: 'upload',
      file: files[i],
    }));

    mutate(
      {
        id: uploadImagesModal.id,
        ...(obj.current.name === i18n.t('dropdown.addImages')
          ? { photos: data }
          : { documents: data }),
      },
      {
        onSuccess: (res: any) => {
          if (res && !!uploadImagesModal.submitClick) {
            uploadImagesModal.submitClick();
          }
        },
        onSettled: () => {
          setOpen(false);
        },
      },
    );
  };

  return (
    <>
      <ModalBase id={'upload-image-modal'} open={open} onClose={handleOnClose}>
        <ModalWrapper>
          <Typography variant={'textL'} weight={'bold'}>
            {obj.current.title}
          </Typography>
          <ModalImages isFlowRow={obj.current.name === i18n.t('dropdown.addDocuments')} isVisible>
            {obj.current.name === i18n.t('dropdown.addImages') &&
              images.map((img: any, i: any) => (
                <ImageGalleryCard
                  key={i}
                  img={img}
                  handleZoomClick={() => handleZoomClick(i)}
                  handleDeleteImageClick={() => handleDeleteImageClick(i)}
                />
              ))}
            {obj.current.name === i18n.t('dropdown.addDocuments') &&
              files.map((file: any, i: any) => {
                return (
                  <FileRow
                    key={i}
                    file={file}
                    handleDeleteFileClick={() => handleDeleteImageClick(i)}
                    withoutBorder={i === 0}
                  />
                );
              })}
            <HideInput>
              <FileInput
                id={'file-input'}
                label={obj.current.name}
                inputRef={inputRef}
                inputProps={{
                  accept:
                    obj.current.name === i18n.t('dropdown.addImages')
                      ? '.jpeg, .jpg, .png, .webp'
                      : '*',
                  multiple: true,
                }}
                handleChangeFiles={handleChangeFiles}
                withoutCompress={obj.current.name !== i18n.t('dropdown.addImages')}
              />
            </HideInput>
          </ModalImages>
          <ModalFooterButtons columns={'1fr'}>
            <Button
              size={'medium'}
              data-color={'primary'}
              onClick={() => inputRef.current.click()}
              justify={'left'}
            >
              {obj.current.name === i18n.t('dropdown.addImages')
                ? i18n.t('button.addImages')
                : i18n.t('button.addDocuments')}
            </Button>
            <Button size={'medium'} data-color={'white'} onClick={handleOnClose}>
              {i18n.t('button.cancel')}
            </Button>
            <Button
              size={'medium'}
              data-color={'primary'}
              onClick={handleSubmitClick}
              disabled={!files.length}
            >
              {obj.current.name === i18n.t('dropdown.addImages')
                ? i18n.t('button.saveImages')
                : i18n.t('button.saveDocuments')}
            </Button>
          </ModalFooterButtons>
        </ModalWrapper>
      </ModalBase>
      <ImageGallery id={'upload-images-modal'} photos={images} startIndex={zoomImageIndex} />
    </>
  );
};
